import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from '@heroicons/react/20/solid';
import { twMerge } from 'tailwind-merge';

import AstroImage from '@/components/ui/custom/AstroImage';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Badge, Button } from '@/components/ui/daisy';
import { Colors } from '@/components/ui/types';
import { CourseTrackLesson } from '@/utils/student-dashboard/courseTrackLesson';

const AREA_BADGE_COLORS: Colors[] = ['info', 'secondary', 'accent'];

export type CourseTrackCardProps = {
  lesson: CourseTrackLesson;
  href?: string;
  className?: string;
};

export default function CourseTrackCard({
  lesson,
  href,
  className,
}: CourseTrackCardProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'studentDashboard.courseTrack',
  });

  return (
    <article
      className={twMerge(
        'flex w-full flex-col gap-6 rounded-3xl bg-base-200 p-6',
        className,
      )}
    >
      <AstroImage
        src={lesson.bannerImg}
        alt={t('bannerAlt', { lesson: lesson.title })}
        className="h-41 w-full shrink-0 rounded-2xl object-cover"
      />
      <div className="flex min-w-0 flex-1 flex-col gap-4">
        <h3 className="text-base font-semibold text-base-content line-clamp-2">
          {lesson.title}
        </h3>
        <div className="flex flex-col gap-1">
          <p className="line-clamp-3 text-sm text-base-content">
            {lesson.description}
          </p>
        </div>
        <ConditionalRenderer condition={lesson.areas.length > 0}>
          <ul className="flex flex-wrap gap-1">
            {lesson.areas.map((area, index) => (
              <li key={area.id}>
                <Badge
                  size="sm"
                  variant="soft"
                  color={AREA_BADGE_COLORS[index % AREA_BADGE_COLORS.length]}
                >
                  {area.name}
                </Badge>
              </li>
            ))}
          </ul>
        </ConditionalRenderer>
        <ConditionalRenderer condition={!!href}>
          <Link to={href ?? ''} className="mt-auto w-full">
            <Button
              color="primary"
              shape="block"
              tabIndex={-1}
              className="rounded-lg"
            >
              {t('access')}
              <ArrowRightIcon className="size-4" />
            </Button>
          </Link>
        </ConditionalRenderer>
      </div>
    </article>
  );
}
